/**
 * Lokal dokumentindex. Lagras i localStorage under en enda nyckel så att vi
 * kan lista alla dokument som bor i denna browser utan att behöva query:a
 * alla IndexedDB-databaser. Den riktiga data:n ligger i Yjs/IndexedDB under
 * `electro-planner:<documentId>`; registret är bara metadata.
 *
 * När backenden landar byter vi bara implementationen mot en tRPC-query —
 * API:t kan förbli detsamma.
 */

const STORAGE_KEY = "electro-planner:documents";
const MAX_ENTRIES = 200;
export const DEFAULT_DOCUMENT_ID = "local";

export type DocumentRecord = {
  id: string;
  title: string;
  createdAt: string;
  lastEditedAt: string;
};

function safeParse(raw: string | null): DocumentRecord[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is DocumentRecord =>
        typeof r?.id === "string" &&
        typeof r?.title === "string" &&
        typeof r?.createdAt === "string" &&
        typeof r?.lastEditedAt === "string",
    );
  } catch {
    return [];
  }
}

function readAll(): DocumentRecord[] {
  if (typeof window === "undefined") return [];
  return safeParse(window.localStorage.getItem(STORAGE_KEY));
}

function writeAll(records: DocumentRecord[]): void {
  if (typeof window === "undefined") return;
  // Trimma äldsta först om vi överskrider taket — osannolikt i praktiken.
  const trimmed = records.slice(0, MAX_ENTRIES);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // Fullt/avstängt localStorage: ignorera, editorn fungerar ändå.
  }
}

export function listDocuments(): DocumentRecord[] {
  return readAll().sort((a, b) => b.lastEditedAt.localeCompare(a.lastEditedAt));
}

export function getDocument(id: string): DocumentRecord | undefined {
  return readAll().find((r) => r.id === id);
}

/** Skapa-om-saknas eller uppdatera en post. Uppdaterar alltid lastEditedAt. */
export function touchDocument(
  id: string,
  patch: Partial<Pick<DocumentRecord, "title">> = {},
): DocumentRecord {
  const now = new Date().toISOString();
  const records = readAll();
  const idx = records.findIndex((r) => r.id === id);
  if (idx >= 0) {
    const existing = records[idx]!;
    const updated: DocumentRecord = {
      ...existing,
      ...patch,
      lastEditedAt: now,
    };
    records[idx] = updated;
    writeAll(records);
    return updated;
  }
  const created: DocumentRecord = {
    id,
    title: patch.title ?? "",
    createdAt: now,
    lastEditedAt: now,
  };
  records.push(created);
  writeAll(records);
  return created;
}

export function renameDocument(id: string, title: string): void {
  const records = readAll();
  const idx = records.findIndex((r) => r.id === id);
  if (idx < 0) return;
  records[idx] = { ...records[idx]!, title };
  writeAll(records);
}

/**
 * Ta bort en post från registret. Tar INTE bort Yjs-data:n ur IndexedDB
 * eftersom det kräver en async Y.IndexeddbPersistence-instans; kallaren
 * får göra det steget separat om det behövs.
 */
export function forgetDocument(id: string): void {
  const records = readAll().filter((r) => r.id !== id);
  writeAll(records);
}

export function createDocumentId(): string {
  const random = Math.random().toString(36).slice(2, 8);
  return `doc-${Date.now().toString(36)}-${random}`;
}
